import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PriceButton } from "@/components/AgentPrices";

/**
 * Modale di ricerca testuale libera.
 * Il cliente può digitare nome e/o descrizione del prodotto che sta cercando.
 * Il backend restituisce i 3 prodotti più simili dall'intero catalogo
 * (incluse le categorie SOLO-VETRINA Attrezzatura e Altri prodotti).
 */
export default function TextSearchModal({ onClose }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // null | {total, results: []}
  const [expanded, setExpanded] = useState(null); // product id currently expanded
  const textareaRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const search = async (e, overrideQuery) => {
    e?.preventDefault();
    const q = (overrideQuery !== undefined ? overrideQuery : query).trim();
    if (!q) {
      toast.error("Inserisci nome o descrizione");
      return;
    }
    setLoading(true);
    setExpanded(null);
    try {
      const { data } = await api.post("/products/search-by-text", { query: q });
      setResults(data);
      if (!data.results || data.results.length === 0) {
        toast.info("Nessun prodotto trovato — prova con parole diverse.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ricerca");
      setResults({ total: 0, results: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="tm-modal fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      data-testid="text-search-modal"
      onClick={onClose}
    >
      <div
        className="tm-card w-full max-w-3xl my-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-slate-800 flex items-start justify-between gap-4">
          <div>
            <div className="tm-tag inline-block mb-3">Ricerca testuale</div>
            <h2 className="font-heading text-2xl md:text-3xl text-white tracking-tighter mb-1">
              Cerca per nome o descrizione
            </h2>
            <p className="text-slate-400 text-sm">
              Digita il nome del prodotto o una descrizione libera: ti mostriamo i 3 più simili dall'intero catalogo TOP-TEC.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-2xl leading-none shrink-0"
            aria-label="Chiudi"
            data-testid="text-search-close"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={search} className="p-6 md:p-8 border-b border-slate-800 space-y-4">
          <div>
            <label className="tm-label">Cosa stai cercando?</label>
            <textarea
              ref={textareaRef}
              rows={3}
              className="tm-input"
              placeholder="Es. silicone resistente al calore per vetro, oppure PANNO MICROFIBRA, oppure disotturante per scarichi…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); search(); }
              }}
              data-testid="text-search-input"
            />
            <p className="text-xs text-slate-500 mt-2 font-mono">
              Invio per cercare · Shift+Invio per andare a capo
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="tm-btn-primary disabled:opacity-50"
              data-testid="text-search-submit"
            >
              {loading ? "Ricerca in corso..." : "Cerca →"}
            </button>
            {results && (
              <button
                type="button"
                onClick={() => { setResults(null); setQuery(""); textareaRef.current?.focus(); }}
                className="tm-btn-outline"
                data-testid="text-search-reset"
              >
                Nuova ricerca
              </button>
            )}
          </div>
        </form>

        {/* Results */}
        {results && (
          <div className="p-6 md:p-8" data-testid="text-search-results">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl text-white tracking-tight">
                {results.results.length > 0
                  ? `Top ${results.results.length} ${results.results.length === 1 ? "risultato" : "risultati"}`
                  : "Nessun risultato"}
              </h3>
              {results.total > results.results.length && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {results.total} prodotti compatibili totali
                </span>
              )}
            </div>

            {results.results.length === 0 ? (
              <div className="tm-card p-6 text-center text-slate-400 text-sm">
                Nessun prodotto corrisponde alla tua ricerca. Prova con sinonimi, nomi di brand (es. "TOP"),
                oppure usa il Product Finder per cercare per categoria.
              </div>
            ) : (
              <div className="space-y-3">
                {results.results.map((p) => {
                  const isOpen = expanded === p.id;
                  return (
                    <div
                      key={p.id}
                      className="tm-card overflow-hidden"
                      data-testid={`text-search-result-${p.id}`}
                    >
                      <div className="flex gap-4 p-4">
                        <div className="w-24 h-24 shrink-0 bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center">
                          {p.image_url ? (
                            <img
                              src={p.image_url}
                              alt={p.name}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <span className="text-slate-600 font-mono text-xs">TOP-TEC</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div className="min-w-0">
                              <div className="font-heading text-base md:text-lg text-white tracking-tight leading-tight">
                                {p.name}
                              </div>
                              {p.brand && (
                                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">
                                  {p.brand}
                                </div>
                              )}
                            </div>
                            <div className="shrink-0 flex items-center gap-2">
                              <span className="font-mono text-[10px] text-orange-400">
                                {p.match_score}
                              </span>
                            </div>
                          </div>
                          <p className={`text-slate-300 text-sm leading-relaxed mt-2 ${isOpen ? "" : "line-clamp-2"}`}>
                            {p.description || "—"}
                          </p>
                          {(p.description || "").length > 150 && (
                            <button
                              type="button"
                              onClick={() => setExpanded(isOpen ? null : p.id)}
                              className="mt-2 font-mono text-[10px] uppercase tracking-widest text-orange-400 hover:text-orange-300"
                              data-testid={`toggle-detail-${p.id}`}
                            >
                              {isOpen ? "— Mostra meno" : "+ Mostra tutto"}
                            </button>
                          )}
                          <div className="mt-3">
                            <PriceButton productId={p.id} productName={p.name} className="!py-2 !px-3 !min-h-0" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
